import { useEffect, useState } from 'react';
import { Search, Download, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { handleExport, slug } from '@/lib/exportCsv';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'partner', label: 'Partners' },
  { value: 'employee', label: 'Employees' },
  { value: 'customer', label: 'Customers' },
  { value: 'online_sales', label: 'Online Sales' },
  { value: 'supplier', label: 'Suppliers' },
];

const COLS = [
  { key: 'date', label: 'Date', format: (v) => String(v || '').slice(0, 10) },
  { key: 'bank_name', label: 'Bank' },
  { key: 'month', label: 'Month' },
  { key: 'description', label: 'Description' },
  { key: 'name', label: 'Name' },
  { key: 'link_type', label: 'Linked Type' },
  { key: 'link_name', label: 'Linked To' },
  { key: 'category', label: 'Category' },
  { key: 'type', label: 'Type' },
  { key: 'amount', label: 'Amount' },
];

/** Global "Search Transactions" across every uploaded statement/month.
 *  Export downloads ONLY the currently filtered results (via shared handleExport). */
export const GlobalTxnSearchCard = ({ onViewStatement }) => {
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    const lt = typeFilter === 'all' ? '' : typeFilter;
    if (term.length < 2 && !lt) { setData(null); return undefined; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/bank-statements/search-transactions?q=${encodeURIComponent(term)}&link_type=${encodeURIComponent(lt)}`);
        setData(r.data);
      } catch {
        setData({ results: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(h);
  }, [q, typeFilter]);

  const rows = data?.results || [];
  return (
    <Card className="rounded-2xl border-border" data-testid="global-txn-search-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Transactions — by description or name, across all months…"
              className="pl-9 rounded-xl"
              data-testid="global-txn-search-input"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="global-txn-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {loading && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
          {data && (
            <Badge variant="outline" className="text-[11px]" data-testid="global-txn-search-count">
              {data.total} match{data.total === 1 ? '' : 'es'}
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            disabled={!rows.length}
            onClick={() => handleExport(rows, `bank_txn_search_${slug(q || typeFilter)}`, COLS, 'transactions')}
            data-testid="global-txn-export-btn"
          >
            <Download size={14} className="mr-1.5" /> Export
          </Button>
        </div>
        {data && (rows.length ? (
          <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr className="text-left text-muted-foreground">
                  <th className="p-2 font-medium">Date</th>
                  <th className="p-2 font-medium">Bank</th>
                  <th className="p-2 font-medium">Month</th>
                  <th className="p-2 font-medium">Description</th>
                  <th className="p-2 font-medium">Name</th>
                  <th className="p-2 font-medium text-right">Amount</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.statement_id}-${i}`} className="border-t border-border hover:bg-muted/40" data-testid={`global-txn-row-${i}`}>
                    <td className="p-2 whitespace-nowrap">{String(r.date || '').slice(0, 10)}</td>
                    <td className="p-2 whitespace-nowrap">{r.bank_name}</td>
                    <td className="p-2 whitespace-nowrap">{r.month}</td>
                    <td className="p-2 max-w-[340px] truncate" title={r.description}>{r.description}</td>
                    <td className="p-2 max-w-[160px] truncate" title={r.name}>
                      {r.name}
                      {r.link_type && <span className="block text-[9px] text-muted-foreground capitalize">↪ {r.link_type}</span>}
                    </td>
                    <td className={`p-2 text-right tabular-nums font-medium ${r.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2">
                      {onViewStatement && (
                        <button type="button" className="text-primary hover:underline" onClick={() => onViewStatement(r.statement_id)} data-testid={`global-txn-view-${i}`}>
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="global-txn-no-results">
            No transactions match your filter across your uploaded statements.
          </p>
        ))}
      </CardContent>
    </Card>
  );
};

export default GlobalTxnSearchCard;
